// @flow
import React from 'react';
import { connect } from 'react-redux';
import { Field, formValueSelector, reduxForm } from 'redux-form';
import { Prompt } from 'react-router-dom';

import StyledForm from '../shared/StyledForm';
import Scrollable from '../shared/Scrollable';

import Button from '../shared/Button';
import Geolocation from '../shared/forms/Geolocation';
import DropdownList from '../shared/forms/DropdownList';
import Select from '../shared/forms/Select';
import TextArea from '../shared/forms/TextArea';
import TextInput from '../shared/forms/TextInput';

import { propertyReferenceIsValid } from '../../redux/reducers/property';

export const validate = (values: Object) => {
    const errors = {};

    if (!values.reference) {
        errors.reference = 'Reference is required';
    } else if (values.reference.length !== 8) {
        errors.reference = 'Reference must be at 8 characters long';
    }

    if (!values.name || values.name.length < 1) {
        errors.name = 'Name is required';
    }

    if (!values.townCity || values.townCity.length < 1) {
        errors.townCity = 'Location is required';
    }

    if (!values.postcode || values.postcode.length < 1) {
        errors.postcode = 'Postcode is required';
    }

    if (!values.businessEstateId || values.businessEstateId.length < 1) {
        errors.businessEstateId = 'Please select a business estate';
    }

    if (!values.nearestStationId || values.nearestStationId.length < 1) {
        errors.nearestStationId = 'Please select the nearest station';
    }

    if (!values.regionId || values.regionId.length < 1) {
        errors.regionId = 'Please select a region';
    }

    if (!values.countyId || values.countyId.length < 1) {
        errors.countyId = 'Please select a county';
    }

    return errors;
};

class ReferenceError extends Error {
    reference: string;

    constructor() {
        super();
        this.reference = 'Reference is already in use.';
    }
}

const asyncValidate = (values, dispatch) => {
    return dispatch(propertyReferenceIsValid(values.reference)).then(action => {
        if (!action.payload.isValid) {
            throw new ReferenceError();
        }
    });
};

let PropertyLocationForm = props => {
    const {
        change,
        boroughs,
        businessEstates,
        counties,
        handleSubmit,
        dirty,
        postcode,
        regions,
        selectedRegionId,
        selectedCountyId,
        stationOptions,
        submitting,
    } = props;

    const regionOptions = regions
        ? Object.keys(regions).map(regionId => {
              return {
                  id: regionId,
                  label: regions[regionId].regionName,
              };
          })
        : [];

    const countyOptions =
        selectedRegionId && selectedRegionId.length > 0 && counties
            ? Object.keys(counties)
                  .filter(countyId => {
                      return (
                          counties[countyId].countyRegionId === selectedRegionId
                      );
                  })
                  .map(countyId => {
                      return {
                          id: countyId,
                          label: counties[countyId].countyName,
                      };
                  })
            : [];

    const boroughOptions =
        selectedCountyId && selectedCountyId.length > 0 && boroughs
            ? Object.keys(boroughs)
                  .filter(boroughId => {
                      return (
                          boroughs[boroughId].boroughCountyId ===
                          selectedCountyId
                      );
                  })
                  .map(boroughId => {
                      return {
                          id: boroughId,
                          label: boroughs[boroughId].boroughName,
                      };
                  })
            : [];

    const businessEstateOptions = businessEstates
        ? Object.keys(businessEstates).map(businessEstateId => {
              return {
                  id: businessEstateId,
                  label: businessEstates[businessEstateId].businessEstateName,
              };
          })
        : [];

    return (
        <StyledForm onSubmit={handleSubmit}>
            <Prompt
                when={dirty && !submitting}
                message="You have made changes that will be lost if you proceed. Are you sure you want to continue?"
            />

            <Scrollable additionalClassName="scroll-area">
                <div className="flex">
                    <div className="col">
                        <Field
                            name="reference"
                            label="Reference"
                            component={TextInput}
                            maxLength={8}
                            normalize={value => value.toUpperCase()}
                            disabled={submitting}
                        />

                        <Field
                            name="name"
                            label="Name"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="address"
                            label="Address"
                            component={TextArea}
                            rows={3}
                            disabled={submitting}
                        />

                        <Field
                            name="townCity"
                            label="Location"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="postcode"
                            label="Postcode"
                            component={TextInput}
                            disabled={submitting}
                        />

                        <Field
                            name="geolocation"
                            component={Geolocation}
                            geocodeSourceValue={postcode}
                            disabled={submitting}
                        />
                    </div>

                    <div className="col">
                        <Field
                            name="regionId"
                            label="Region"
                            component={Select}
                            disabled={submitting}
                            onChange={e => {
                                change('countyId', '');
                                change('boroughId', '');
                            }}
                        >
                            <option value="">Please select...</option>
                            {regionOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        <Field
                            name="countyId"
                            label="County"
                            component={Select}
                            disabled={
                                submitting ||
                                !selectedRegionId ||
                                countyOptions.length === 0
                            }
                            onChange={e => {
                                change('boroughId', '');
                            }}
                        >
                            <option value="">
                                {!selectedRegionId
                                    ? 'Select a region first...'
                                    : 'Please select...'}
                            </option>
                            {countyOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        <Field
                            name="boroughId"
                            label="Borough"
                            disabled={
                                submitting ||
                                !selectedCountyId ||
                                boroughOptions.length === 0
                            }
                            component={Select}
                        >
                            <option value="">
                                {!selectedCountyId
                                    ? 'Select a county first...'
                                    : 'Please select...'}
                            </option>
                            {boroughOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        <Field
                            name="businessEstateId"
                            label="Business Estate"
                            component={Select}
                            disabled={submitting}
                        >
                            <option value="">Please select...</option>
                            {businessEstateOptions.map(option =>
                                <option key={option.id} value={option.id}>
                                    {option.label}
                                </option>
                            )}
                        </Field>

                        <Field
                            name="nearestStationId"
                            label="Nearest Station"
                            component={DropdownList}
                            disabled={submitting}
                            data={stationOptions}
                        />
                    </div>
                </div>
            </Scrollable>

            <div className="sticky-footer">
                <Button type="submit" disabled={submitting}>
                    {submitting ? 'Saving...' : 'Save & Continue'}
                </Button>
            </div>
        </StyledForm>
    );
};

PropertyLocationForm = reduxForm({
    form: 'propertyEditLocation',
    validate,
    asyncValidate,
    asyncBlurFields: ['reference'],
})(PropertyLocationForm);

const selector = formValueSelector('propertyEditLocation');

const mapStateToProps = state => {
    return {
        postcode: selector(state, 'postcode'),
        selectedRegionId: selector(state, 'regionId'),
        selectedCountyId: selector(state, 'countyId'),
    };
};

export default connect(mapStateToProps)(PropertyLocationForm);



// WEBPACK FOOTER //
// ./src/components/PropertyForms/PropertyLocationForm.js